import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { reportBuilderApi } from '../api/reportBuilderApi';

export type GenerateStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * POST /api/eraf/report-builder/reports/{id}/generate?format=...
 * 바이너리(xlsx/pdf) 반환 → 브라우저 다운로드 트리거
 * audit-log excel 다운로드 패턴 동일하게 구현
 */
export function useGenerateReport() {
  const [statusMap, setStatusMap] = useState<Record<string, GenerateStatus>>({});
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const queryClient = useQueryClient();

  async function doGenerate(id: number, format: 'excel' | 'pdf') {
    const key = `${id}-${format}`;
    setStatusMap((prev) => ({ ...prev, [key]: 'loading' }));
    setErrorCode(undefined);
    try {
      const { data, filename } = await reportBuilderApi.generate(id, format);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setStatusMap((prev) => ({ ...prev, [key]: 'success' }));
      // 이력 목록 무효화 (생성 후 이력 갱신)
      void queryClient.invalidateQueries({ queryKey: ['report-builder', 'generations'] });
      setTimeout(() => setStatusMap((prev) => ({ ...prev, [key]: 'idle' })), 3000);
    } catch (err) {
      const apiErr = err as { code?: string };
      setErrorCode(apiErr.code ?? 'UNKNOWN');
      setStatusMap((prev) => ({ ...prev, [key]: 'error' }));
    }
  }

  function getStatus(id: number, format: 'excel' | 'pdf'): GenerateStatus {
    return statusMap[`${id}-${format}`] ?? 'idle';
  }

  function isPending(id: number, format: 'excel' | 'pdf'): boolean {
    return getStatus(id, format) === 'loading';
  }

  return { doGenerate, getStatus, isPending, errorCode };
}
